import React, { useState } from 'react';
import { Auth } from '../api/apiClient';
import useDocumentTitle from '../hooks/useDocumentTitle';

const RULES = [
  { id: 'length', label: 'Mindestens 8 Zeichen', test: (p) => p.length >= 8 },
  { id: 'upper', label: 'Mindestens ein Großbuchstabe', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'Mindestens ein Kleinbuchstabe', test: (p) => /[a-z]/.test(p) },
  { id: 'digit', label: 'Mindestens eine Zahl', test: (p) => /[0-9]/.test(p) },
];

function PasswortAendernPage({ onPasswordChanged }) {
  useDocumentTitle('Passwort ändern');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const rulesPassed = RULES.every((r) => r.test(newPassword));
  const passwordsMatch = newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!rulesPassed) {
      setError('Das neue Passwort erfüllt nicht alle Anforderungen.');
      return;
    }
    if (!passwordsMatch) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setSaving(true);
    let changeResponse;
    try {
      changeResponse = await Auth.changePassword(oldPassword, newPassword);
    } catch (err) {
      setError(err.message || 'Fehler beim Ändern des Passworts.');
      setSaving(false);
      return;
    }

    // Das alte Token wird durch die Passwortänderung serverseitig ungültig.
    // Die API liefert deshalb direkt ein neues Token zurück.
    if (changeResponse?.access_token) {
      localStorage.setItem('authToken', changeResponse.access_token);
    } else {
      // Fallback für ältere API-Versionen ohne Token in der Antwort
      try {
        const storedUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        if (storedUser.schuetzenId) {
          const loginResponse = await Auth.login(String(storedUser.schuetzenId), newPassword);
          const newToken = loginResponse.access_token || loginResponse.token;
          if (newToken) {
            localStorage.setItem('authToken', newToken);
            localStorage.setItem('currentUser', JSON.stringify(loginResponse.user || storedUser));
          }
        }
      } catch {
        // Re-Login fehlgeschlagen – Nutzer muss sich neu anmelden
      }
    }

    if (onPasswordChanged) onPasswordChanged();
    setSaving(false);
  };

  return (
    <div className="login-screen min-h-screen flex flex-col items-center justify-center gap-6 px-5 py-8 font-sans">
      <style>{`
        .login-screen {
          background: linear-gradient(160deg, #3a9a4a 0%, #2e7d3a 55%, #164a20 100%);
        }
        @media (prefers-color-scheme: dark) {
          .login-screen {
            background: linear-gradient(160deg, #14431f 0%, #0c2f15 60%, #04160a 100%);
          }
          .login-screen .login-card {
            background-color: #16231a;
            box-shadow: 0 1.5rem 3rem rgba(0, 0, 0, .5);
          }
          .login-screen .login-title { color: #e8f0ea; }
          .login-screen .field-label { color: #a9bdae; }
          .login-screen .field-input {
            background-color: #0f1a12;
            border-color: rgba(255, 255, 255, .12);
            color: #e8f0ea;
          }
          .login-screen .field-input::placeholder { color: #6f8577; }
          .login-screen .login-company { color: rgba(255, 255, 255, .7); }
        }
      `}</style>

      <main className="login-card card w-full max-w-[25rem] rounded-2xl border-0 px-8 py-10 text-center shadow-[0_1.5rem_3rem_rgba(15,40,20,0.28)] sm:px-9">
        {/* Logo / Brand */}
        <img
          src="/rwk-logo.png"
          alt="RWK Logo"
          className="mx-auto mb-2 h-32 w-32 object-contain"
        />

        <h1 className="login-title mb-6 text-xl font-bold tracking-tight text-slate-900">Passwort ändern</h1>

        {error && (
          <div className="alert-error mb-4 rounded-lg text-left">
            <svg className="h-4 w-4 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 text-left">
          <div className="field-group">
            <label className="field-label" htmlFor="oldPassword">Aktuelles Passwort</label>
            <input
              id="oldPassword"
              type="password"
              className="field-input"
              placeholder="Aktuelles Passwort"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              disabled={saving}
              required
              autoFocus
            />
          </div>

          <div className="field-group">
            <label className="field-label" htmlFor="newPassword">Neues Passwort</label>
            <input
              id="newPassword"
              type="password"
              className="field-input"
              placeholder="Neues Passwort"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={saving}
              required
            />
            {newPassword && (
              <ul className="mt-2 space-y-1">
                {RULES.map((r) => (
                  <li key={r.id} className={`flex items-center gap-1.5 text-xs ${r.test(newPassword) ? 'text-green-600' : 'text-slate-400'}`}>
                    {r.test(newPassword)
                      ? <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><polyline points="20 6 9 17 4 12" /></svg>
                      : <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><circle cx="12" cy="12" r="9" /></svg>
                    }
                    {r.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="field-group">
            <label className="field-label" htmlFor="confirmPassword">Passwort bestätigen</label>
            <input
              id="confirmPassword"
              type="password"
              className={`field-input ${confirmPassword && !passwordsMatch ? 'border-red-400' : ''}`}
              placeholder="Passwort wiederholen"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={saving}
              required
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-red-500 mt-1">Passwörter stimmen nicht überein.</p>
            )}
          </div>

          <button
            type="submit"
            className="btn-primary btn w-full mt-2"
            disabled={saving || !rulesPassed || !passwordsMatch}
          >
            {saving && <span className="spinner" />}
            {saving ? 'Wird gespeichert…' : 'Passwort speichern'}
          </button>
        </form>
      </main>

      <p className="login-company m-0 text-center text-xs leading-normal text-white/85">
        RWK Gruppe<br />Diözesanverband Paderborn
      </p>
    </div>
  );
}

export default PasswortAendernPage;
